import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { getApiBase, getWebsiteLeadSourceHeaders } from "@/lib/apiBase";

export default function Partner() {
  const [form, setForm] = React.useState({ name: "", phone: "", location: "", experience: "", message: "" });
  const [submitting, setSubmitting] = React.useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.location) {
      toast.error("Please fill name, phone and location");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${getApiBase()}/partner`, form, { headers: getWebsiteLeadSourceHeaders() });
      toast.success("Application submitted! Our team will reach out shortly.");
      setForm({ name: "", phone: "", location: "", experience: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    "Access to Wateriia's established supply chain",
    "High demand and recurring orders",
    "Technology support (app + logistics system)",
    "Branding and operational guidance",
    "Scalable business model with growth potential",
  ];
  const partners = ["Local entrepreneurs", "Existing distributors", "Retailers expanding into delivery"];

  return (
    <section id="partner" className="py-24 md:py-32 water-grad">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow">Partner With Wateriia</div>
            <h2 className="serif h-display mt-4 text-4xl sm:text-5xl lg:text-6xl text-[#0b1d2a]">
              Grow With Wateriia — <br />
              <span className="italic text-[#0369a1]">Open a Dark Store in your area.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 text-slate-600">
            Join India's expanding distribution network by running a local fulfillment center — optimized for app-based water orders and fast last-mile delivery.
          </div>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2 rounded-3xl overflow-hidden border border-sky-100 aspect-[16/9]">
              <img
                src="https://customer-assets.emergentagent.com/job_tap-order-delivered/artifacts/d33y789k_WhatsApp%20Image%202026-04-30%20at%207.15.11%20PM.jpeg"
                alt="Wateriia Dark Store"
                className="h-full w-full object-cover"
              />
            </div>

            <div className="rounded-3xl bg-white border border-sky-100 p-7">
              <div className="eyebrow">What is a Dark Store?</div>
              <p className="mt-3 text-[#0b1d2a]">
                A local fulfillment hub dedicated to fast, app-based water delivery — no walk-in customers, all logistics.
              </p>
            </div>

            <div className="rounded-3xl bg-white border border-sky-100 p-7">
              <div className="eyebrow">Ideal Partners</div>
              <ul className="mt-3 space-y-2 text-[#0b1d2a] text-sm">
                {partners.map((p, i) => (
                  <li key={i} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#0ea5e9]" /> {p}</li>
                ))}
              </ul>
            </div>

            <div className="sm:col-span-2 rounded-3xl bg-[#0b1d2a] text-white p-7 md:p-8">
              <div className="eyebrow text-sky-300">Benefits of Partnering</div>
              <ul className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/85">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-sky-300 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="partner-form"
            className="lg:col-span-5 rounded-3xl bg-white border border-sky-100 p-7 md:p-8"
          >
            <div className="eyebrow">Apply to Partner</div>
            <h3 className="serif h-display mt-2 text-2xl sm:text-3xl text-[#0b1d2a]">Start your dark store.</h3>

            <div className="mt-6 grid gap-4">
              <FormField label="Name" id="p-name">
                <input
                  id="p-name" data-testid="partner-input-name" required
                  value={form.name} onChange={set("name")}
                  className="w-full bg-transparent border-0 border-b border-sky-200 focus:border-[#0369a1] focus:ring-0 outline-none py-2 text-[#0b1d2a]"
                />
              </FormField>
              <FormField label="Phone" id="p-phone">
                <input
                  id="p-phone" data-testid="partner-input-phone" required
                  value={form.phone} onChange={set("phone")}
                  className="w-full bg-transparent border-0 border-b border-sky-200 focus:border-[#0369a1] focus:ring-0 outline-none py-2 text-[#0b1d2a]"
                />
              </FormField>
              <FormField label="Location" id="p-location">
                <input
                  id="p-location" data-testid="partner-input-location" required
                  value={form.location} onChange={set("location")}
                  className="w-full bg-transparent border-0 border-b border-sky-200 focus:border-[#0369a1] focus:ring-0 outline-none py-2 text-[#0b1d2a]"
                />
              </FormField>
              <FormField label="Business Experience" id="p-exp">
                <input
                  id="p-exp" data-testid="partner-input-experience"
                  value={form.experience} onChange={set("experience")}
                  className="w-full bg-transparent border-0 border-b border-sky-200 focus:border-[#0369a1] focus:ring-0 outline-none py-2 text-[#0b1d2a]"
                />
              </FormField>
              <FormField label="Message" id="p-message">
                <textarea
                  id="p-message" data-testid="partner-input-message" rows={3}
                  value={form.message} onChange={set("message")}
                  className="w-full bg-transparent border-0 border-b border-sky-200 focus:border-[#0369a1] focus:ring-0 outline-none py-2 text-[#0b1d2a] resize-none"
                />
              </FormField>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="partner-submit"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0b1d2a] text-white py-3.5 font-semibold hover:bg-[#0369a1] transition disabled:opacity-60"
            >
              {submitting ? "Submitting..." : "Apply to Partner"} <ArrowRight size={16} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function FormField({ label, id, children }) {
  return (
    <label htmlFor={id} className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
      {children}
    </label>
  );
}
